import * as React from 'react';
import {
  ENTITY_NAME_KOTLIN_STRING
} from '@config/index';
import {
  FrameMapping,
  Attribute,
  FrameItem,
  ReferenceNode,
  ParameterType,
  CompositeInfo,
  FrameMappingContent,
  Reference,
  ConversionMode
} from '@interface/index';
import {
  DeleteOutlined
} from '@ant-design/icons';
import { Popover, Popconfirm, Spin, message } from 'antd';
import { cloneDeep, debounce } from 'lodash';
import {
  NiModal,
  NiSelect,
  NiForm,
  NiFormItem,
  discardModal,
  NiRadio,
  AddButton,
  NiTable,
  HelperPopover
} from '@component/index';
import { FormattedMessage } from 'react-intl';
import { localizedString } from '@locale/index';
import {
  isEqual,
  ParameterMapStore
} from '@lib/index';

export interface FrameMappingModalProps {
  disabled?: boolean;
  visible: boolean;
  title: string;
  frame: FrameItem;
  availableComposites: CompositeInfo[];
  data: FrameMapping;
  selectedParameters: Attribute[];
  confirm: (next: FrameMapping) => void;
  close: () => void;
}

export interface FrameMappingModalState {
  data: FrameMapping;
  loading: boolean;
}

export class FrameMappingModal extends React.Component<FrameMappingModalProps, FrameMappingModalState> {
  public state: FrameMappingModalState = {
    data: {
      contents: []
    },
    loading: false
  };

  componentDidUpdate(prevProps: any) {
    if (!isEqual(prevProps, this.props)) {
      this.onInit();
    }
  }

  componentDidMount() {
    this.onInit();
  }

  render() {
    const props = this.props;
    const { disabled } = props;
    const { loading } = this.state;

    return (
      <NiModal
        open={this.props.visible}
        width={'60%'}
        centered
        title={
          <div className="data-query-header">
            <h3
              style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}
            >
              <FormattedMessage id="frame_mapping" />
            </h3>
          </div>
        }
        okText={(this.props.data.inheritedFromCompositeId) &&
          !(this.props.frame as any).isInterface &&
          <Popconfirm
            title={
              <span>{localizedString('data_query_inherit_copy_popconfirm')}
                <HelperPopover localizedKey="data_query_inherit_copy_popover" />
              </span>
            }
            onConfirm={this.handleOk}
          >
            <a href="#">{localizedString('common_confirm')}</a>
          </Popconfirm> || undefined
        }
        onOk={disabled ||
          (props.data.inheritedFromCompositeId &&
            !(props.frame as any).isInterface)
          ? undefined : () => {
            this.handleOk();
          }}
        okButtonProps={!!props.disabled ||
          (props.data.inheritedFromCompositeId && (props.frame as any).isInterface)
          ? {
            type: 'ghost',
            disabled: true
          } : undefined}
        onCancel={this.handleCancel}
      // closable={false}
      >
        <Spin spinning={loading}>
          <div className="db-modal">{this.bodyComp()}</div>
        </Spin>
      </NiModal>
    );
  }

  onInit = async () => {
    const {
      data
    } = this.props;


    this.setState({
      data: cloneDeep(data)
    });
  };

  dirtyCheck = () => {
    const { data } = this.props;
    const state = this.state;
    return !isEqual(data || [], state.data);
  };

  handleOk = debounce(() => {
    const {
      data
    } = this.state;

    if (!data.targetFrame || !data.targetFrame.id) {
      message.warning(localizedString('frame_mapping_target_frame_hint'));
      return;
    }

    for (const content of data.contents) {

      if (!content.targetSlot || !content.targetSlot.id) {
        message.warning(localizedString('frame_mapping_target_slot_hint'));
        return;
      }


      if (!content.mappingSlot || !content.mappingSlot.id) {
        message.warning(localizedString('frame_mapping_mapping_slot_hint'));
        return;
      }
    }

    const next = cloneDeep(data);
    this.props.confirm(next);
  }, 500);

  handleCancel = () => {
    if (this.dirtyCheck()) {
      discardModal(() => {
        this.clearState();
        this.props.close();
      });
      return;
    }

    this.clearState();
    this.props.close();
  };

  getSlotIdFromRef = (ref: Reference): any => {
    if (!ref || !ref.length) {
      return null;
    }

    return ref[ref.length - 1].id;
  }

  getRefFromSlotId = (slotId: string) => {
    const { selectedParameters } = this.props;

    const p = selectedParameters.find((spm: Attribute) => spm.id === slotId);

    if (!p) {
      return [];
    }

    return [{
      id: p.id,
      name: p.name,
      type: ParameterType.ATTRIBUTE
    }];
  }

  bodyComp = () => {
    const state = this.state;
    const props = this.props;
    const { disabled, selectedParameters, availableComposites } = props;
    const { data } = state;

    const formItems: NiFormItem[] = [
      {
        key: 'target-frame',
        label: localizedString('frame_mapping_target_frame'),
        labelExtra: <NiSelect
          disabled={disabled}
          value={data.targetFrame && data.targetFrame.id}
          placeholder={localizedString('frame_mapping_target_frame_placeholder')}
          onChange={(value: string) => {
            if (value && value.length) {
              this.updateData('targetFrame', {
                id: value,
                type: ParameterType.COMPOSITE
              });

            } else {
              this.updateData('targetFrame', null);
            }
          }}
        >
          {
            availableComposites
              .filter((item: CompositeInfo) => !!item.importedFromService)
              .map((item: CompositeInfo) =>
                <NiSelect.Option key={item.id} value={item.id || ''}>
                  {
                    item.name
                  }
                </NiSelect.Option>)
          }
        </NiSelect>
      },
      {
        key: 'contents',
        label: localizedString('frame_mapping_contents'),
        extra: <AddButton
          disabled={disabled}
          onClick={() => {
            const nextContents = cloneDeep(data.contents || []);
            nextContents.push({ conversionMode: ConversionMode.Label });
            this.updateData('contents', nextContents);
          }}
        />,
        content: this.getContentsDom()
      }
    ];

    return (
      <div className="state-update-modal">
        <NiForm
          titleline
          direction="vertical"
          items={formItems}
        />
      </div>
    );
  };

  getContentsDom = () => {
    const { disabled, availableComposites, selectedParameters } = this.props;
    const { data } = this.state;

    const composite: CompositeInfo | undefined = availableComposites.find((item: CompositeInfo) =>
      data.targetFrame && item.id === data.targetFrame.id);

    return <NiTable
      style={{ flex: 1 }}
      pagination={false}
      dataSource={data.contents}
      columns={[
        {
          title: localizedString('frame_mapping_target_slot'),
          dataIndex: 'targetSlot',
          width: '33%',
          render: (_: ReferenceNode, record: FrameMappingContent, index: number) => {

            return <NiSelect
              disabled={disabled}
              value={record.targetSlot && record.targetSlot.id}
              placeholder={localizedString('common_select')}
              onChange={(value: string) => {

                const nextContents = cloneDeep(data.contents);
                nextContents[index].targetSlot = {
                  id: value,
                  type: ParameterType.ATTRIBUTE,
                  name: ''
                };

                this.updateData('contents', nextContents);
              }}
            >
              {
                composite &&
                composite.attributes
                  .map((item: Attribute) =>
                    <NiSelect.Option key={item.id} value={item.id || ''}>
                      {
                        item.name
                      }
                    </NiSelect.Option>)
              }
            </NiSelect>;
          }
        },
        {
          title: localizedString('frame_mapping_mapping_slot'),
          dataIndex: 'mappingSlot',
          width: '33%',
          render: (_: ReferenceNode, record: FrameMappingContent, index: number) => {

            return <NiSelect
              disabled={disabled}
              value={record.mappingSlot && record.mappingSlot.id}
              placeholder={localizedString('common_select')}
              onChange={(value: string) => {

                const nextContents = cloneDeep(data.contents);
                nextContents[index].mappingSlot = {
                  id: value,
                  type: ParameterType.ATTRIBUTE,
                  name: ''
                };

                this.updateData('contents', nextContents);
              }}
            >
              {
                selectedParameters
                  .map((item: Attribute) =>
                    <NiSelect.Option key={item.id} value={item.id || ''}>
                      {
                        item.name
                      }
                    </NiSelect.Option>)
              }
            </NiSelect>;
          }
        },
        {
          title: localizedString('frame_mapping_conversion_mode'),
          dataIndex: 'conversionMode',
          width: '33%',
          render: (mode: ReferenceNode, record: FrameMappingContent, index: number) => {

            const attr = record.targetSlot &&
              record.targetSlot.id &&
              ParameterMapStore.get(record.targetSlot.id);

            return <NiRadio
              disabled={disabled || (attr && attr.proto && attr.proto.name !== ENTITY_NAME_KOTLIN_STRING || false)}
              style={{
                marginTop: 4
              }}
              value={mode || ConversionMode.Label}
              onChange={(e: any) => {
                const nextContents = cloneDeep(data.contents);
                nextContents[index].conversionMode = e.target.value;
                this.updateData('contents', nextContents);
              }}
              keyValue={[
                { key: ConversionMode.Label, value: localizedString('frame_mapping_conversion_mode_label') },
                { key: ConversionMode.Expression, value: localizedString('frame_mapping_conversion_mode_expression') }
              ]}
            />;
          }
        },
        {
          title: '',
          dataIndex: '',
          width: 100,
          key: 'operation',
          render: (_: ReferenceNode, record: FrameMappingContent, index: number) => {
            return !disabled &&
              <div className="icon-area">
                {<DeleteOutlined
                  className="delete-icon"
                  onClick={() => {
                    const nextContents = cloneDeep(data.contents || []);
                    nextContents.splice(index, 1);
                    this.updateData('contents', nextContents);
                  }}
                />}
              </div>;
          }
        }
      ]}
    />;
  }


  updateData = (key: string, value: any) => {
    const next: any = cloneDeep(this.state.data);
    next[key] = value;
    this.setState({ data: next });
  }


  clearState = () => {
    this.setState({
      data: {
        contents: []
      }
    });
  };
}
